/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Soft UI Dashboard PRO React components

// Soft UI Dashboard PRO React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Soft UI Dashboard PRO React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Soft UI Dashboard PRO React routes
import routes from "routes";

import Utils from "Utils";
import { validCookieExists, getUserData, getAuthorizationHeader, getError } from "Utils/helpers";
import { getData } from "Utils/server";

// Images
//import brand from "assets/images/favicon.ico";
import brandFull from "assets/images/logo_full.png";
import brandShort from "assets/images/favicon.png";
import brandFull_dark from "assets/images/logo_full_dark.png";
import brandShort_dark from "assets/images/favicon_dark.png";

let previousLocation = "";
let currentLocation = "";

// Soft UI Dashboard PRO React contexts
import { useSoftUIController, setMiniSidenav, setOpenConfigurator, setPermissions, setProviders, setSports, setMarketGroups } from "context";


const globalCss = require("assets/theme/base/globals");

export default function App() {
  //console.log("app run");
  const [controller, dispatch] = useSoftUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    permissions,
    platform,
    activeTemplate,
    sports_numbers,
    sports,
    providers,
    marketGroups,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { hash, pathname } = useLocation();
  const [userPermissions, setUserPermissions] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([...routes]);

  const css = globalCss ? globalCss.default : null;
  const customColors = css.customColors;
  const darkmode = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;

  const navigate = useNavigate();
  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Navigate to any path
  const routeChange = (path) => {
    navigate(path);
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    //console.log("landing Url: ", pathname);
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;

    // Login check
    if (!validCookieExists()) {
      routeChange("/login");
    }
    if (pathname == "/") routeChange("/dashboard");

    if (!previousLocation && !currentLocation) {
      previousLocation = pathname;
      currentLocation = pathname;
    } else {
      previousLocation = currentLocation;
      currentLocation = pathname;
    }

    // console.log("living from ...", previousLocation);
    // console.log("going to ...", currentLocation);

    // if (previousLocation != currentLocation) {
    //   console.log("must abort all previous requests");
    // }
  }, [pathname]);

  useEffect(() => {
    if (pathname.indexOf("dashboard") > 0 && userPermissions.length > 0) {
      // console.log(JSON.stringify(userPermissions));
      // console.log("check Permissions: ", checkPermission("dashboard"));
      if (userPermissions && checkPermission("dashboard") === false) routeChange("/account");
    }
  }, [userPermissions]);

  useEffect(() => {
    // console.log("permissions: ", permissions);
    let tmpUserPermissions = [...permissions];
    if (tmpUserPermissions.length === 0) {
      fetchPermissions();
    }
    setUserPermissions(tmpUserPermissions);

    if (!providers && tmpUserPermissions.length > 0) fetchProviders();
  }, [permissions]);

  useEffect(() => {
    if (!sports && permissions && permissions.length > 0) fetchSports();
  }, [sports, permissions]);

  useEffect(() => {
    if (!marketGroups && permissions && permissions.length > 0) fetchMarketGroups();
  }, [marketGroups, permissions]);

  // Create Routes visualization
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (checkPermission(route.permission)) {
        if (route.dynamic && sports_numbers) {
          //console.log("Collecting Dynamic Routes from App");
          let dynamicRoute = Utils.collectDynamicRoutes(route.route, null, null, sports_numbers);
          route.icon = dynamicRoute.icon;
          if (route.name.indexOf("(") > -1) route.name = route.name.split("(")[0].trim();
          route.name = route.name + " " + dynamicRoute.name;
          route.collapse = dynamicRoute.collapse;
        }
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          return <Route exact path={route.route} element={route.component} key={route.key} />;
        }
      } else {
        if (route.permission === "dashboard" && userPermissions.length > 0) {
          if (userPermissions.includes("super")) {
            return <Route exact path="*" element={<Navigate to="/dashboard" />} key={route.key} />;
          } else {
            return <Route exact path="*" element={<Navigate to="/account" />} key={route.key} />;
          }
        }
      }

      return null;
    });

  const filterRoutes = (allRoutes) => {
    // console.log("all routes : ", routes);
    // console.log("filtering routes : ", allRoutes);
    return allRoutes.filter((route) => {
      if (checkPermission(route.permission)) {
        if (route.collapse) {
          route.collapse = route.collapse.filter((routeCollapse) => {
            if (checkPermission(routeCollapse.permission)) return routeCollapse;
          });

          route.collapse.forEach((leaf) => {
            if (leaf.collapse) {
              leaf.collapse = leaf.collapse.filter((leafCollapse) => {
                if (checkPermission(leafCollapse.permission)) return leafCollapse;
              });
            }
          });
        }
        return route;
      }
    });
  };

  const checkPermission = (permission) => {
    // console.log("permission: ", permission);
    // console.log("userPermissions: ", userPermissions);

    if (permission === true) return true;
    if (permission === false) return false;
    if (userPermissions.includes("super")) return true;
    return userPermissions.includes(permission);
  };

  const fetchPermissions = async () => {
    // console.log("calling API");
    let tmpError = getError();
    let userPermissions = [];
    let user = getUserData();
    if (!user) return;
    if (tmpError && tmpError.config && tmpError.config.uri.indexOf("routepermissions") > -1) {
      routeChange("/login");
    }
    let getPermissionsEndPoint = `/api/v1/users/${user.id}/routepermissions`;
    let response = await getData(getPermissionsEndPoint, {
      Authorization: getAuthorizationHeader(),
    });

    if (response != null) {
      userPermissions = response.data ? response.data : ["No permissions"];
      if (userPermissions.length === 0) userPermissions.push("No permissions");
    }
    setUserPermissions(userPermissions);
    if (userPermissions.length > 0) {
      setPermissions(dispatch, userPermissions);
    }
  };

  const fetchProviders = async () => {
    let tmpError = getError();
    let userPermissions = [];
    let user = getUserData();
    if (!user) return;
    if (tmpError && tmpError.config && tmpError.config.uri.indexOf("routepermissions") > -1) {
      routeChange("/login");
    }
    let getProvidersEndpoint = `/api/v1/entities/allproviders`;
    let response = await getData(getProvidersEndpoint, { Authorization: getAuthorizationHeader() });
    if (response != null) {
      let providersData = [];
      providersData = providersData.concat(
        response.data.map((item) => {
          if (item.color) Utils.createCssProvidersColors(customColors, item);
          //Utils.createCssProvidersButtons(customColors, item);
          return { value: item.id, label: item.name, color: item.color, info: item.info };
        })
      );
      setProviders(dispatch, providersData);
    }
  };

  const fetchSports = async () => {
    let getSportsEndpoint = `/api/v1/entities/allsports`;
    let response = await getData(getSportsEndpoint, { Authorization: getAuthorizationHeader() });
    if (response != null) {
      setSports(dispatch, response.data);
    }
  };

  const fetchMarketGroups = async () => {
    let getMarketGroupsEndpoint = `/api/v1/entities/availableMarketGroups`;
    let response = await getData(getMarketGroupsEndpoint, {
      Authorization: getAuthorizationHeader(),
    });
    if (response != null) {
      setMarketGroups(dispatch, response.data);
    }
  };

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brandShort={darkmode ? brandShort_dark : brandShort}
              brandFull={darkmode ? brandFull_dark : brandFull}
              brandName="Backoffice"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brandShort={darkmode ? brandShort_dark : brandShort}
            brandFull={darkmode ? brandFull_dark : brandFull}
            brandName="Backoffice"
            routes={filterRoutes(filteredRoutes)}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          {/* <Configurator />
          {configsButton} */}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>{getRoutes(routes)}</Routes>
    </ThemeProvider>
  );
}
